import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {TableColumnsType} from 'antd';
import {Button, InputNumber, message, Modal, Select, Space, Table} from 'antd';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import EditableDrawer from 'components/EditableDrawer';
import SingleSelect from 'components/SingleSelect';
import InputFilterItem from 'components/InputFilterItem';
import { useQuery, useMutation } from '@apollo/client';
import { GET_TMS_CARRIERS_BY_WAREHOUSE_ID, GetTmsCarriersByWarehouseIdResponse } from '../graphql/queries/getTmsCarriers';
import { GET_HANDOVER_CARRIERS_BY_WAREHOUSE_ID, GetHandoverCarriersByWarehouseIdResponse } from '../graphql/queries/getHandoverCarriers';
import { GET_ALL_STATIONS, GetAllStationsResponse, TmsStation,} from '../graphql/queries/getStationListByBusinessType';
import { CREATE_TMS_ROUTE_WITH_WAYPOINTS, UPDATE_TMS_ROUTE_WITH_WAYPOINTS } from '../graphql/mutations/routeMutations';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import {
    TmsBusinessTypeEnum,
    TmsRouteTypeEnum,
    TmsStationSourceEnum,
    TmsStatusEnum,
    WaypointTypeEnum,
    HandoverTypeEnum,
    ALL_CENTER_WAREHOUSE_ID,
} from '../../../../enums/TmsEnum';
import { isExternalStationRoute, getExternalStationType } from '../utils/externalStation';

import { t } from '../../../../i18n';
import RouteWaypointSection from './RouteWaypointSection';
import { StationRow, WarehouseOption, DrawerMode } from '../types/routeFormTypes';
import { useRouteFormValidation } from '../hooks/useRouteFormValidation';
import { useRouteFormSubmit } from '../hooks/useRouteFormSubmit';
import { useRouteFormOptions } from '../hooks/useRouteFormOptions';
import { useRouteFormInit } from '../hooks/useRouteFormInit';
import { isCarrierValidForRouteType } from '../../hooks/useFilteredCarriers';

interface RouteFormDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (values: any) => void;
    initialValues?: any;
    mode: DrawerMode;
    statusOptions: { label: string; value: number }[];
    routeTypeOptions: { label: string; value: number }[];
    businessTypeOptions: { label: string; value: number }[];
    isHandoverTab?: boolean;
    // 点击行时父级据 record 算好的"是否展示途经点配置"，编辑/详情态用作首帧定调，避免异步数据到位前的闪烁
    isShowConfig?: boolean;
}

// 途经点最大数量
const WAYPOINT_MAX_COUNT = 3;

/** 从站点列表中按 id 查找外部站点 */
const findExternalStationById = (list: TmsStation[], id: number): TmsStation | undefined =>
    list.find(c => c.id != null && c.id === id && c.stationSource === TmsStationSourceEnum.EXTERNAL);

/** 从站点列表中按 name 查找外部站点 */
const findExternalStationByName = (list: TmsStation[], name: string): TmsStation | undefined =>
    list.find(c => c.stationSource === TmsStationSourceEnum.EXTERNAL && (c.name || '').trim() === name);

const RouteFormDrawer: React.FC<RouteFormDrawerProps> = ({
    visible,
    onClose,
    onSubmit,
    initialValues,
    mode,
    statusOptions,
    routeTypeOptions,
    businessTypeOptions,
    isHandoverTab = false,
    isShowConfig,
}) => {
    const [filters, setFilters] = useState({
        routeType: undefined as number | undefined,
        businessType: TmsBusinessTypeEnum.OwnRoute as number | undefined,
        carrierId: undefined as string | undefined,
        status: TmsStatusEnum.Active as number | undefined
    });
    const [routeCode, setRouteCode] = useState<string | undefined>(undefined);
    const [handoverType, setHandoverType] = useState<number | undefined>(undefined);
    // 从 WarehouseContext 获取当前 Hub（TmsLayout 提供）
    const { currentWarehouse } = useWarehouseContext();

    // 从 currentWarehouse 中提取 hubId 和 siteType
    const hubId = currentWarehouse?.id ? String(currentWarehouse.id) : null;
    const [tableData, setTableData] = useState<StationRow[]>([
        { key: WaypointTypeEnum.Origin, type: WaypointTypeEnum.Origin, station: '', travelTime: 0, dwellTime: 0, distance: 0 },
        { key: WaypointTypeEnum.Destination, type: WaypointTypeEnum.Destination, station: '', travelTime: 0, dwellTime: 0, distance: 0 }
    ]);

    // 查询承运商
    const { data: carriersData, loading: carriersLoading } = useQuery<GetTmsCarriersByWarehouseIdResponse>(GET_TMS_CARRIERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : null
        },
        skip: !currentWarehouse?.id
    });

    // 查询交接方（编辑模式带出已选交接方，即使其已被禁用，保证回显与交接方类型联动正常）
    const { data: handoverCarriersData } = useQuery<GetHandoverCarriersByWarehouseIdResponse>(GET_HANDOVER_CARRIERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : 0,
            includeCarrierId: mode === DrawerMode.Edit && initialValues?.carrierId != null
                ? Number(initialValues.carrierId)
                : undefined,
        },
        skip: !visible || filters.routeType !== TmsRouteTypeEnum.Handover || !currentWarehouse?.id
    });

    // 根据交接方类型过滤交接方列表，未选类型时显示全部；不可用项（已禁用 / 已移出本仓）置灰并打 tag，可回显但不可重新选择
    const handoverCarrierOptions = useMemo(() => {
        const list = handoverCarriersData?.getHandoverCarriersByWarehouseId ?? [];
        const filtered = handoverType !== undefined
            ? list.filter(hc => hc.carrierType === handoverType)
            : list;
        const warehouseId = currentWarehouse?.id ? String(currentWarehouse.id) : '';
        return filtered.map(hc => {
            // 交接方 status：1=启用（与 TmsStatusEnum.Active 同值）
            const isActive = hc.status === TmsStatusEnum.Active;
            const inWarehouse = hc.centerWarehouseIds.includes(ALL_CENTER_WAREHOUSE_ID) || hc.centerWarehouseIds.includes(warehouseId);
            const unavailable = !isActive || !inWarehouse;
            return {
                label: hc.carrierName,
                value: hc.id,
                disabled: unavailable,
                secondaryLabel: unavailable ? String(t('tms.route.drawer.unavailableTag')) : undefined,
            };
        });
    }, [handoverCarriersData, handoverType, currentWarehouse]);

    // 是否为交接类型路线：创建模式只看表单内选择的routeType；编辑模式用isHandoverTab立即判断+ref缓存避免关闭时闪跳
    const isHandoverRouteRef = useRef(mode === DrawerMode.Create ? false : isHandoverTab);
    if (visible || filters.routeType !== undefined) {
        isHandoverRouteRef.current = filters.routeType === TmsRouteTypeEnum.Handover;
    }
    const isHandoverRoute = (mode !== DrawerMode.Create && isHandoverTab) || isHandoverRouteRef.current;

    // 仅 OwnRoute 与交接类型互斥
    const isHandoverExcluded = filters.businessType === TmsBusinessTypeEnum.OwnRoute;
    const isOwnRouteExcluded = isHandoverRoute;

    // 交接路线选本地尾程商时不展示配置
    const isLocalLastMileHandover = isHandoverRoute
        && handoverType === HandoverTypeEnum.LocalLastMileCarrier;
    // 编辑/详情态 handoverType 需等交接方列表异步解析，解析前用点击时父级传入的 isShowConfig 定调，避免"先展示后隐藏"的开场闪烁；
    // 解析完成或创建态用实时计算，保证用户切换交接方类型时联动
    const showWaypointSection = (mode !== DrawerMode.Create && handoverType === undefined && isShowConfig !== undefined)
        ? isShowConfig
        : !isLocalLastMileHandover;

    // 站点模式：Temu GA/SCF、TikTok 交接使用外部站点，其余使用仓库
    const isExternalStationMode = isExternalStationRoute(filters.businessType, filters.routeType);

    // 一次查询所有站点（warehouse + external_station），前端按 stationSource / stationType 过滤
    const { data: stationData } = useQuery<GetAllStationsResponse>(GET_ALL_STATIONS, {
        skip: !visible,
        // no-cache：内部站点和外部站点 id 可能重复，Apollo 按 __typename+id 归一化会导致数据被覆盖
        fetchPolicy: 'no-cache',
    });

    const stationList = stationData?.getAllStations ?? [];

    // 当前 Hub 是否测试仓：Hub 自身也在 getAllStations 的内部站点里，无需额外查询
    const currentHubIsTest = useMemo(() => {
        if (!hubId) return false;
        return stationList.find(item =>
            item.stationSource === TmsStationSourceEnum.INTERNAL && String(item.id) === hubId
        )?.isTest ?? false;
    }, [stationList, hubId]);

    // 从全量站点数据派生 warehouseOptions（内部站点，始终可用）
    // 注意：这里必须保持全量（不按 isTest 过滤），编辑态回显和提交时的 sortingCode→id 反解都依赖它
    const warehouseOptions: WarehouseOption[] = useMemo(() => {
        return stationList
            .filter(item => item.stationSource === TmsStationSourceEnum.INTERNAL)
            .map(item => ({
                id: item.id != null ? String(item.id) : '0',
                sortingCode: item.sortingCode?.trim() || '',
                parentSiteId: item.parentSiteId != null ? String(item.parentSiteId) : '',
                siteType: item.siteType ?? 0,
                name: item.name?.trim(),
                isTest: item.isTest ?? false,
            }));
    }, [stationList]);

    // 从全量站点数据派生 externalStationOptions（外部站点，按 stationType 过滤）
    const filteredExternalStations = useMemo(() => {
        const targetStationType = getExternalStationType(filters.businessType, filters.routeType);
        if (targetStationType == null) return [];
        return stationList.filter(item =>
            item.stationSource === TmsStationSourceEnum.EXTERNAL &&
            item.stationType === targetStationType &&
            // 测试仓只能选测试站点，正式仓只能选正式站点
            (item.isTest ?? false) === currentHubIsTest
        );
    }, [stationList, filters.businessType, currentHubIsTest]);

    const externalStationNameList = useMemo(() => {
        if (!isExternalStationMode) return [];
        const codes = filteredExternalStations
            .map(item => item.sortingCode?.trim())
            .filter((c): c is string => !!c && c.length > 0);
        return [...new Set(codes)].sort((a, b) => a.localeCompare(b));
    }, [isExternalStationMode, filteredExternalStations]);

    const externalStationOptions: WarehouseOption[] = useMemo(() => {
        if (!isExternalStationMode) return [];
        return filteredExternalStations
            .filter(item => item.sortingCode?.trim())
            .map(item => ({
                id: item.id != null ? String(item.id) : '0',
                sortingCode: item.sortingCode!.trim(),
                parentSiteId: '',
                siteType: 0,
                name: item.name!.trim(),
                externalStationId: item.id ?? undefined,
                isTest: item.isTest ?? false,
            }));
    }, [isExternalStationMode, filteredExternalStations]);

    const resolveExtWaypointIdFromRow = useCallback(
        (row: StationRow): number | undefined => {
            if (row.extWaypointId != null) {
                return row.extWaypointId;
            }
            const stationName = (row.station || '').trim();
            if (!stationName) {
                return undefined;
            }
            const cfg = findExternalStationByName(stationList, stationName);
            return cfg?.id != null ? cfg.id : undefined;
        },
        [stationList]
    );

    // 校验当前 carrierId 是否对指定条件有效，无效则清空
    const clearInvalidCarrierId = (next: { carrierId?: string | undefined }, routeType: number | undefined) => {
        if (next.carrierId && carriersData?.getTmsCarriersByWarehouseId) {
            if (!isCarrierValidForRouteType(carriersData.getTmsCarriersByWarehouseId, next.carrierId, routeType)) {
                next.carrierId = undefined;
            }
        }
    };

    const handleBusinessTypeChange = useCallback((value: string) => {
        const parsedValue = value ? Number(value) : undefined;
        setFilters(prev => {
            // 交接方类型不预设默认值，切换业务来源时清空，由用户自选
            if (prev.routeType === TmsRouteTypeEnum.Handover) {
                setHandoverType(undefined);
            }
            const next = { ...prev, businessType: parsedValue };
            if (parsedValue === TmsBusinessTypeEnum.TemuGA || parsedValue === TmsBusinessTypeEnum.TikTok) {
                next.carrierId = undefined;
            }
            clearInvalidCarrierId(next, prev.routeType);
            return next;
        });
    }, [carriersData]);

    const handleRouteTypeChange = useCallback((value: string) => {
        const parsedValue = value ? Number(value) : undefined;
        if (parsedValue === TmsRouteTypeEnum.Handover) {
            setRouteCode(undefined);
        }
        // 交接方类型不预设默认值，切换路线类型时清空，由用户自选
        setHandoverType(undefined);
        setFilters(prev => {
            const newFilters = {...prev, routeType: parsedValue, carrierId: undefined};
            clearInvalidCarrierId(newFilters, parsedValue);
            return newFilters;
        });
    }, [carriersData]);

    // 变更操作
    const [createRouteWithWaypoints, { loading: createLoading }] = useMutation(CREATE_TMS_ROUTE_WITH_WAYPOINTS);
    const [updateRouteWithWaypoints, { loading: updateLoading }] = useMutation(UPDATE_TMS_ROUTE_WITH_WAYPOINTS);

    // 保存初始值用于比较是否有修改（仅在编辑模式下）
    const initialCarrierIdRef = useRef<string | undefined>(undefined);
    const initialTableDataRef = useRef<StationRow[]>([]);

    // 初始化表单数据 + 编辑模式同步关联字段
    useRouteFormInit({
        mode,
        visible,
        initialValues,
        currentWarehouse,
        stationData,
        warehouseOptions,
        carriersData,
        handoverCarriersData,
        filters,
        setFilters,
        setRouteCode,
        setHandoverType,
        setTableData,
        initialCarrierIdRef,
        initialTableDataRef,
    });

    // 使用选项过滤 hook
    const {
        carrierOptions,
        filteredWarehouseOptions,
        filteredDestinationOptions,
    } = useRouteFormOptions({
        filters,
        carriersData: carriersData?.getTmsCarriersByWarehouseId,
        warehouseOptions,
        externalStationNameList,
        hubId,
        currentHubIsTest,
        isExternalStationMode,
        mode,
        setTableData,
    });

    const effectiveWaypointStationOptions = isExternalStationMode ? externalStationOptions : filteredWarehouseOptions;
    const effectiveDestinationStationOptions = isExternalStationMode ? externalStationOptions : filteredDestinationOptions;

    // 使用校验 hook
    const { validateForm, validateWaypointDuplicates } = useRouteFormValidation({
        filters,
        routeCode,
        showWaypointSection,
        tableData,
        resolveExtWaypointIdFromRow,
    });

    // 计算总用时和总距离
    // totalHours: 包含所有行的travelTime + 所有行的dwellTime（包括起点和终点）
    const totalHours = tableData.reduce((sum, row) => {
        const travelTime = row.travelTime || 0;
        const dwellTime = row.dwellTime || 0;
        return sum + travelTime + dwellTime;
    }, 0).toFixed(1);
    const totalMiles = tableData.reduce((sum, row) => sum + row.distance, 0).toFixed(1);

    // 使用提交 hook
    const { performSubmit, checkForChanges } = useRouteFormSubmit({
        filters,
        tableData,
        routeCode,
        mode,
        initialValues,
        showWaypointSection,
        currentWarehouse,
        warehouseOptions,
        handoverCarriersData,
        totalHours,
        totalMiles,
        initialCarrierIdRef,
        initialTableDataRef,
        resolveExtWaypointIdFromRow,
        validateForm,
        validateWaypointDuplicates,
        createRouteWithWaypoints,
        updateRouteWithWaypoints,
        onSubmit,
    });

    // 编辑模式修改确认弹窗
    const showModificationConfirm = (onConfirm: () => Promise<void>) => {
        Modal.confirm({
            title: t('tms.route.messages.routeModificationConfirmTitle'),
            content: (
                <div>
                    <div>{t('tms.route.messages.routeModificationConfirmContent')}</div>
                    <div>{t('tms.route.messages.routeModificationConfirmItem1')}</div>
                    <div>{t('tms.route.messages.routeModificationConfirmItem2')}</div>
                    <div>{t('tms.route.messages.routeModificationConfirmItem3')}</div>
                </div>
            ),
            okText: t('tms.route.messages.confirm'),
            cancelText: t('tms.route.messages.cancel'),
            okButtonProps: { style: { backgroundColor: '#722ed1', borderColor: '#722ed1' } },
            cancelButtonProps: { style: { color: '#722ed1', borderColor: '#722ed1' } },
            onOk: onConfirm,
        });
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;

            if (mode === DrawerMode.Edit && checkForChanges()) {
                showModificationConfirm(() => performSubmit());
                return;
            }

            await performSubmit();
        } catch (error: any) {
            console.error('Failed to save route:', error);
            message.error(error.message || t('tms.route.messages.failedToSaveRoute'));
        }
    };

    // 途经点数量（不含始发站和终点）
    const waypointCount = tableData.filter(row => row.type === WaypointTypeEnum.Waypoint).length;

    const handleDeleteRow = useCallback((key: string | number) => {
        setTableData(prevData => prevData.filter(item => item.key !== key));
    }, []);

    // 表格列定义
    const columns: TableColumnsType<StationRow> = useMemo(() => [
        {
            title: '',
            key: 'empty',
            width: 100,
            render: (_, record) => {
                if (record.type === WaypointTypeEnum.Origin) return String(t('tms.route.drawer.origin'));
                if (record.type === WaypointTypeEnum.Destination) return String(t('tms.route.drawer.destination'));
                if (record.type === WaypointTypeEnum.Waypoint) {
                    // 计算当前途经点的序号
                    const waypointIndex = tableData
                        .filter(row => row.type === WaypointTypeEnum.Waypoint)
                        .findIndex(row => row.key === record.key);
                    return String(t('tms.route.drawer.waypointX', { index: waypointIndex + 1 }));
                }
                return '';
            },
        },
        {
            title: String(t('tms.route.drawer.station')),
            dataIndex: 'station',
            key: 'station',
            width: 160,
            render: (value: string, record: StationRow) => {
                if (record.type === WaypointTypeEnum.Origin) {
                    return value || '-';
                }
                // 编辑模式下，终点不允许编辑
                if (record.type === WaypointTypeEnum.Destination && mode === DrawerMode.Edit) {
                    return value || '-';
                }
                // 终点使用 effectiveDestinationStationOptions；途经点使用 effectiveWaypointStationOptions
                const options = record.type === WaypointTypeEnum.Destination
                    ? effectiveDestinationStationOptions
                    : effectiveWaypointStationOptions;
                return (
                    <Select
                        style={{ width: '100%' }}
                        value={value || undefined}
                        onChange={(val) => {
                            setTableData(prevData =>
                                prevData.map(row => {
                                    if (row.key !== record.key) {
                                        return row;
                                    }
                                    if (!isExternalStationMode) {
                                        return { ...row, station: val || '' };
                                    }
                                    if (!val) {
                                        return { ...row, station: '', extWaypointId: undefined };
                                    }
                                    const opt = options.find(w => w.sortingCode === val);
                                    const tid = opt?.externalStationId;
                                    return {
                                        ...row,
                                        station: val || '',
                                        ...(tid != null ? { extWaypointId: tid } : { extWaypointId: undefined }),
                                    };
                                })
                            );
                        }}
                        options={options.map(w => ({
                            label: isExternalStationMode ? w.name! : w.sortingCode,
                            value: isExternalStationMode ? w.name! : w.sortingCode,
                        }))}
                        placeholder={String(t('tms.route.drawer.selectStation'))}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    />
                );
            },
        },
        {
            title: String(t('tms.route.drawer.travelTime')),
            dataIndex: 'travelTime',
            key: 'travelTime',
            render: (value: number, record: StationRow) => {
                if (record.type === WaypointTypeEnum.Origin) return '-';
                return (
                    <InputNumber
                        style={{ width: '100%' }}
                        value={value}
                        onChange={(val) => {
                            setTableData(prevData =>
                                prevData.map(row =>
                                    row.key === record.key ? { ...row, travelTime: val || 0 } : row
                                )
                            );
                        }}
                        min={0}
                        step={0.1}
                        precision={1}
                    />
                );
            },
        },
        {
            title: String(t('tms.route.drawer.dwellTime')),
            dataIndex: 'dwellTime',
            key: 'dwellTime',
            render: (value: number, record: StationRow) => {
                return (
                    <InputNumber
                        style={{ width: '100%' }}
                        value={value}
                        onChange={(val) => {
                            setTableData(prevData =>
                                prevData.map(row =>
                                    row.key === record.key ? { ...row, dwellTime: val || 0 } : row
                                )
                            );
                        }}
                        min={0}
                        step={0.1}
                        precision={1}
                    />
                );
            },
        },
        {
            title: String(t('tms.route.drawer.distance')),
            dataIndex: 'distance',
            key: 'distance',
            render: (value: number, record: StationRow) => {
                if (record.type === WaypointTypeEnum.Origin) return '-';
                return (
                    <InputNumber
                        style={{ width: '100%' }}
                        value={value}
                        onChange={(val) => {
                            setTableData(prevData =>
                                prevData.map(row =>
                                    row.key === record.key ? { ...row, distance: val || 0 } : row
                                )
                            );
                        }}
                        min={0}
                        step={0.1}
                        precision={1}
                    />
                );
            },
        },
        {
            title: String(t('tms.route.drawer.actions')),
            key: 'actions',
            width: 100,
            render: (_, record) => (
                record.type === WaypointTypeEnum.Waypoint ? (
                    <Space>
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteRow(record.key)}
                        />
                    </Space>
                ) : null
            ),
        },
    ], [
        t,
        tableData,
        effectiveDestinationStationOptions,
        effectiveWaypointStationOptions,
        isExternalStationMode,
        mode,
        handleDeleteRow,
    ]);

    const handleAddStation = () => {
        if (waypointCount >= WAYPOINT_MAX_COUNT) return;

        // 查找终点的索引
        const destIndex = tableData.findIndex(row => row.type === WaypointTypeEnum.Destination);

        const newRow: StationRow = {
            key: `${WaypointTypeEnum.Waypoint}-${Date.now()}`,
            type: WaypointTypeEnum.Waypoint,
            station: '',
            travelTime: 0,
            dwellTime: 0,
            distance: 0,
        };

        // 插入到终点之前
        const newData = [...tableData];
        newData.splice(destIndex, 0, newRow);
        setTableData(newData);
    };

    const drawerTitle = mode === DrawerMode.Create
        ? String(t('tms.route.drawer.createTitle'))
        : mode === DrawerMode.Edit
            ? (initialValues?.routeName || String(t('tms.route.drawer.editTitle')))
            : String(t('tms.route.drawer.viewTitle'));

    // 重置放到关闭动画结束后执行（afterOpenChange open=false），避免抽屉滑出过程中表单字段闪回默认值造成闪烁
    const handleAfterOpenChange = (open: boolean) => {
        if (open) return;
        // 重置表格数据到初始状态
        setTableData([
            { key: WaypointTypeEnum.Origin, type: WaypointTypeEnum.Origin, station: '', travelTime: 0, dwellTime: 0, distance: 0 },
            { key: WaypointTypeEnum.Destination, type: WaypointTypeEnum.Destination, station: 'Destination', travelTime: 0, dwellTime: 0, distance: 0 }
        ]);
        // 重置筛选项
        setFilters({
            routeType: undefined as number | undefined,
            businessType: TmsBusinessTypeEnum.OwnRoute as number | undefined,
            carrierId: undefined as string | undefined,
            status: TmsStatusEnum.Active as number | undefined
        });
        setRouteCode(undefined);
        setHandoverType(undefined);
        // 重置初始值引用
        isHandoverRouteRef.current = false;
        initialCarrierIdRef.current = undefined;
        initialTableDataRef.current = [];
    };

    return (
        <EditableDrawer
            title={drawerTitle}
            visible={visible}
            onClose={onClose}
            afterOpenChange={handleAfterOpenChange}
            onSubmit={handleSubmit}
            confirmDisabled={false}
            width={1000}
            confirmLoading={createLoading || updateLoading}
        >
            {/* Filter Section */}
            <div className="bg-white mb-4">
                <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center">
                        <SingleSelect
                            label={String(t('tms.route.drawer.businessType'))}
                            options={businessTypeOptions
                                .filter(option => !isOwnRouteExcluded || option.value !== TmsBusinessTypeEnum.OwnRoute)
                                .map(option => ({
                                    label: option.label,
                                    value: String(option.value)
                                }))}
                            value={filters.businessType !== undefined ? filters.businessType.toString() : ''}
                            onChange={handleBusinessTypeChange}
                            placeholder={String(t('tms.route.drawer.selectBusinessType'))}
                            required
                            forceHorizontal
                            className="flex-1"
                            spanClassName="w-[6em]"
                            disabled={mode === DrawerMode.Edit}
                        />
                    </div>

                    <div className="flex items-center">
                        <SingleSelect
                            label={String(t('tms.route.drawer.type'))}
                            options={routeTypeOptions
                                .filter(option => !isHandoverExcluded || option.value !== TmsRouteTypeEnum.Handover)
                                .map(option => ({
                                    label: option.label,
                                    value: String(option.value)
                                }))}
                            value={filters.routeType !== undefined ? filters.routeType.toString() : ''}
                            onChange={handleRouteTypeChange}
                            placeholder={String(t('tms.route.drawer.selectType'))}
                            required
                            forceHorizontal
                            className="flex-1"
                            spanClassName="w-[6em]"
                            disabled={mode === DrawerMode.Edit}
                        />
                    </div>
                    {isHandoverRoute ? (
                        <div className="flex items-center">
                            <SingleSelect
                                label={String(t('tms.route.drawer.handoverType'))}
                                options={[
                                    { label: String(t('tms.route.handoverTypes.localLastMileCarrier')), value: String(HandoverTypeEnum.LocalLastMileCarrier) },
                                    { label: String(t('tms.route.handoverTypes.linehaulCarrier')), value: String(HandoverTypeEnum.LinehaulCarrier) },
                                ]}
                                value={handoverType !== undefined ? String(handoverType) : ''}
                                onChange={(value) => {
                                    setHandoverType(value ? Number(value) : undefined);
                                    setFilters(prev => ({...prev, carrierId: undefined}));
                                }}
                                placeholder={String(t('tms.route.drawer.selectHandoverType'))}
                                required
                                forceHorizontal
                                className="flex-1"
                                spanClassName="w-[6em]"
                                disabled={mode === DrawerMode.Edit}
                            />
                        </div>
                    ) : (
                        <div>
                            <InputFilterItem
                                label={String(t('tms.route.drawer.routeCode'))}
                                value={routeCode ?? ''}
                                onChange={(event) => {
                                    const sanitizedValue = event.target.value.replace(/[^a-zA-Z0-9]/g, '');
                                    setRouteCode(sanitizedValue);
                                }}
                                required
                                forceHorizontal
                                spanClassName="w-[6em]"
                            />
                        </div>
                    )}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="flex items-center">
                        {isHandoverRoute ? (
                            <SingleSelect
                                label={String(t('tms.route.drawer.handoverCarrier'))}
                                options={handoverCarrierOptions}
                                value={filters.carrierId ?? ''}
                                onChange={(value) => {
                                    setFilters(prev => ({...prev, carrierId: value || undefined}));
                                }}
                                placeholder={String(t('tms.route.drawer.selectHandoverCarrier'))}
                                required
                                forceHorizontal
                                className="flex-1"
                                spanClassName="w-[6em]"
                            />
                        ) : (
                            <SingleSelect
                                label={String(t('tms.route.drawer.carrier'))}
                                options={carrierOptions}
                                value={filters.carrierId ?? ''}
                                onChange={(value) => {
                                    setFilters(prev => ({...prev, carrierId: value || undefined}));
                                }}
                                placeholder={String(t('tms.route.drawer.selectCarrier'))}
                                required
                                forceHorizontal
                                className="flex-1"
                                spanClassName="w-[6em]"
                                disabled={carriersLoading}
                            />
                        )}
                    </div>
                    <div className="flex items-center">
                        <SingleSelect
                            label={String(t('tms.route.drawer.status'))}
                            options={statusOptions.map(option => ({label: option.label, value: String(option.value)}))}
                            value={filters.status !== undefined ? filters.status.toString() : ''}
                            onChange={(value) => {
                                const parsedValue = value ? Number(value) : undefined;
                                setFilters(prev => ({...prev, status: parsedValue}));
                            }}
                            placeholder={String(t('tms.route.drawer.selectStatus'))}
                            required
                            forceHorizontal
                            className="flex-1"
                            spanClassName="w-[6em]"
                        />
                    </div>
                </div>
            </div>

            {showWaypointSection && (
                <RouteWaypointSection
                    totalHours={totalHours}
                    totalMiles={totalMiles}
                    columns={columns}
                    tableData={tableData}
                    waypointCount={waypointCount}
                    maxWaypointCount={WAYPOINT_MAX_COUNT}
                    onAddWaypoint={handleAddStation}
                />
            )}
        </EditableDrawer>
    );
};

export default RouteFormDrawer;
